import { Component, Inject, OnInit, signal, WritableSignal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { isBrownfieldTrigger, serializeTriggerConfiguration } from 'src/app/common/playbookUtils';
import { PlaybookTriggerConfigBase } from 'src/app/models/api/playbooks/playbook.model';
import { TriggerTypes } from 'src/app/models/api/playbooks/triggers/triggerTypes.enum';
import {
  PlaybooksTriggerConfigInput,
  PlaybooksTriggerInput,
} from 'src/app/models/componentInputs/playbooksTriggerInput.interface';
import { PlaybooksTriggerOutput } from 'src/app/models/popups/outputs/playbooksTriggerOutput.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { PlaybookTemplatesService } from 'src/app/services/templates/playbook/playbook-templates.service';

interface TriggerOptions {
  name: string;
  type: TriggerTypes;
}

@Component({
  selector: 'app-playbooks-trigger',
  templateUrl: './playbooks-trigger.component.html',
  styleUrls: ['./playbooks-trigger.component.scss'],
})
export class PlaybooksTriggerComponent implements OnInit {
  public selectedType?: TriggerTypes;

  public supportedTypes = TriggerTypes;
  public fg = new UntypedFormGroup({});
  public typesOptions: TriggerOptions[] = [];

  public triggerConfig?: PlaybookTriggerConfigBase;
  public triggerFormInput: PlaybooksTriggerConfigInput;
  public isEdit = false;
  public isConfigurationValid: WritableSignal<boolean> = signal<boolean>(true);

  public resourceTypeOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  constructor(
    public dialogRef: MatDialogRef<PlaybooksTriggerComponent>,
    @Inject(MAT_DIALOG_DATA) public triggerInput: PlaybooksTriggerInput,
    private readonly _beautifyPipe: BeautifyDisplayStringPipe,
    private readonly _playbookTemplatesService: PlaybookTemplatesService
  ) {
    this._createTypeOptions();
    this.dialogRef.disableClose = true;

    this.dialogRef.backdropClick().subscribe(() => {
      this.closeWindow();
    });

    if (triggerInput.trigger) {
      this.selectedType = triggerInput.trigger.triggerType;
      this.triggerConfig = triggerInput.trigger.configuration;
      this.isEdit = true;

      this.triggerFormInput = {
        integration: this.triggerInput.integration,
        triggerType: triggerInput.trigger.triggerType,
        triggerConfig: this.triggerConfig,
      } as PlaybooksTriggerConfigInput;
    }
  }

  ngOnInit() {
    this.resourceTypeOptions$ = this._playbookTemplatesService.getResourceTypeOptions$();
  }

  public typeOnSelection(newType: TriggerTypes) {
    if (newType !== this.selectedType) {
      this.fg = new UntypedFormGroup({});
      this._resetTriggerOnChange();

      this.triggerFormInput = {
        integration: this.triggerInput.integration,
        triggerType: newType,
        triggerConfig: this.triggerConfig,
      } as PlaybooksTriggerConfigInput;
      this.selectedType = newType;
    }
  }

  public closeWindow() {
    this.dialogRef.close({} as PlaybooksTriggerOutput);
  }

  public createOrUpdate() {
    if (this.isConfigurationValid()) {
      const configuration = serializeTriggerConfiguration(this.fg.value);

      this.dialogRef.close({
        trigger: {
          triggerType: this.selectedType,
          configuration: configuration as PlaybookTriggerConfigBase,
        },
      } as PlaybooksTriggerOutput);
    }
  }

  public setFormValidation(value: boolean) {
    this.isConfigurationValid.set(value);
  }

  private _createTypeOptions() {
    this.typesOptions = Object.keys(TriggerTypes)
      .filter((v) => isNaN(Number(v)))
      .filter((v) => !isBrownfieldTrigger(TriggerTypes[v as keyof typeof TriggerTypes])) // This component should not show brownfield triggers.
      .map(
        (triggerType) =>
          ({
            type: TriggerTypes[triggerType as keyof typeof TriggerTypes],
            name: this._beautifyPipe.transform(triggerType),
          }) as TriggerOptions
      );

    if (this.typesOptions.length == 1 && !this.isEdit) {
      this.typeOnSelection(this.typesOptions[0].type);
    }
  }

  private _resetTriggerOnChange() {
    this.selectedType = undefined;
    this.triggerConfig = undefined;
  }
}
